import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import {
  Cpu,
  LineChart,
  Brain,
  MessageSquareText,
  X,
} from 'lucide-react';

// Feature data with moreContent for back side
const features = [
  {
    title: 'AI-Powered Process Automation',
    description:
      'We implement intelligent automation solutions using AI to optimize repetitive tasks, reduce errors, and boost operational efficiency.',
    icon: Cpu,
    delay: 200,
    moreContent:
      'For a manufacturing client, we replaced a time-consuming manual process of downloading invoices, entering details into the ERP, and archiving files with a fully automated AI-powered workflow. Using AI Builder, invoices are now read, validated, updated in the ERP, and stored in organized folders automatically',
  },
  {
    title: 'Predictive Analytics & ML Modeling',
    description:
      'Our machine learning models analyze historical data to forecast trends and provide actionable insights.',
    icon: LineChart,
    delay: 300,
    moreContent:
      'A retail client struggled with fluctuating demand and overstock issues, relying on manual spreadsheets for sales forecasting. A solution can be implemented where a predictive analytics model using Machine Learning, trained on historical sales, seasonal trends, and promotional data. The model will provide weekly demand forecasts.',
  },
  {
    title: 'Machine Learning Solutions',
    description:
      'We design and implement tailored AI/ML models to accelerate your business growth.',
    icon: Brain,
    delay: 400,
    moreContent:
      'A manufacturing client faced high rejection rates due to manual quality checks that were slow and inconsistent. We developed a machine learning model trained on thousands of product images to detect defects in real time. Integrated with their production line, the system now flags faulty items instantly,',
  },
  {
    title: 'Natural Language Processing',
    description:
      'We build NLP solutions like chatbots, sentiment analysis, and document parsing.',
    icon: MessageSquareText,
    delay: 500,
    moreContent:
      'A global e-commerce client was struggling with slow response times as customer support agents manually sorted thousands of daily queries. An implementation has been done with NLP-based solution using Microsoft Azure Cognitive Services to automatically read, understand, and classify incoming messages by intent and urgency.',
  },
];

const FeatureCard = ({ 
  title, 
  description, 
  Icon, 
  delay, 
  isVisible, 
  moreContent,
  isFlipped,
  onFlip,
  index
}) => {
  const controls = useAnimation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      const initialTimeout = setTimeout(() => {
        controls.start({
          x: [0, -5, 5, -5, 5, 0],
          transition: { duration: 0.6, ease: 'easeInOut' },
        });
      }, delay + 500);

      const interval = setInterval(() => {
        controls.start({
          x: [0, -5, 5, -5, 5, 0],
          transition: { duration: 0.6, ease: 'easeInOut' },
        });
      }, 5000);

      return () => {
        clearTimeout(initialTimeout);
        clearInterval(interval);
      };
    }
  }, [isVisible, controls, delay]);

  useEffect(() => {
    if (isFlipped) {
      // Auto-close after 10 seconds
      timeoutRef.current = setTimeout(() => {
        onFlip(null);
      }, 8000);
    } else {
      clearTimeout(timeoutRef.current);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isFlipped, onFlip]);

  const flipCard = () => {
    onFlip(isFlipped ? null : index);
  };

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0, y: 10 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: delay / 1000, duration: 0.5 }}
    >
      <div className="relative w-full h-96 perspective-1000">
        <motion.div
          className="relative w-full h-full transition-transform duration-500 transform-style-preserve-3d"
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ 
            duration: 0.5,
            ease: [0.4, 0, 0.2, 1]
          }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Front Side */}
          <div
            className="absolute w-full h-full backface-hidden bg-white border border-gray-200 rounded-2xl p-6 flex flex-col"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <motion.div animate={controls} initial={{ x: 0 }} className="mb-6">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center transition-colors duration-300">
                <Icon className="w-6 h-6 text-green-600" />
              </div>
            </motion.div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 leading-relaxed mb-4 flex-grow">{description}</p>
            <button
              onClick={flipCard}
              className="text-sm cursor-pointer text-blue-600 hover:underline mt-auto self-start"
            >
              Learn More →
            </button>
          </div>

          {/* Back Side */}
          <div
            className="absolute w-full h-full backface-hidden bg-white border border-gray-200 rounded-2xl p-6 flex flex-col"
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            <button
              onClick={flipCard}
              aria-label="Close"
              className="absolute cursor-pointer top-4 right-4 p-1 rounded-full hover:bg-gray-200 transition"
            >
              <X className="w-5 h-5 text-gray-600 " />
            </button>

            <div >
              <span className="text-blue-600 font-medium text-sm uppercase tracking-wider">
                Use Cases
              </span>
              <p className="text-gray-700 text-base leading-relaxed mt-1">{moreContent}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const ScriptlyAILanding = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [flippedIndex, setFlippedIndex] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleFlip = (index) => {
    setFlippedIndex(index);
  };

  return (
    <div className="min-h-96 bg-gray-50 py-20  sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
     {/* <div className="mb-4 text-center lg:text-start">
  <span className="text-4xl font-bold lg:text-xl md:text-md text-black  tracking-wider uppercase">
   Gen AI <span className='text-blue-600'>& ML</span> 
  </span>
</div> */}

<div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-12 text-center lg:text-left">
  {/* <div className="lg:max-w-4xl mx-auto lg:mx-0">
    <h2 className="text-md max-w-4xl md:text-xl lg:text- font-bold text-gray-900 leading-tight mb-0">
      Scriptly AI evolving
      <br />
      with your content
    </h2>
  </div> */}
  
    {/* <div className="lg:max-w-xl lg:flex-shrink-0 mx-auto  lg:mx-0">
      <h3 className="text-sm text-center md:text-base text-gray-600 leading-relaxed mb-6 ">
        Harness advanced Generative AI and machine learning to automate processes, deliver predictive insights, and enable personalized experiences at scale.
      </h3>
    </div> */}
</div>


 <div className="bg-gray-50  sm:px-6 lg:px-8 mb-6">
  <div className="max-w-5xl mx-auto text-center ">
    
    {/* {Title } */}
    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
      Gen AI <span className="text-blue-600">& ML</span>
    </h2>

    {/* { Subtitle }
    <h2 className="text-sm md:text-xl font-semibold text-gray-900 mb-4">
      Scriptly AI evolving with your content
    </h2> */}

    {/* { Description } */}
    <p className="text-gray-600 text-lg md:text-md  max-w-3xl mx-auto">
      Harness advanced Generative AI and machine learning to automate processes, deliver predictive insights,
      and enable personalized experiences at scale.
    </p>

  </div>
</div> 



        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map(({ title, description, icon, delay, moreContent }, index) => (
            <FeatureCard
              key={index}
              title={title}
              description={description}
              Icon={icon}
              delay={delay}
              isVisible={isVisible}
              moreContent={moreContent}
              isFlipped={flippedIndex === index}
              onFlip={handleFlip}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScriptlyAILanding;